<template>
  <main-container class="model-predict-evaluation-container" title="モデル適用結果の評価" :subTitle="modelName">
    <div slot="main-content">
      <form-container>
        <form-item-container label="評価データ選択" :tip="dsLabelTip" annotation="* 500MBまで">
          <field-container :class="{ 'has-error': dsValidateError }">
            <field-upload-ds method="evaluate_predict"
                             v-model="ds"
                             :url="`${this.$session.get('API_URL')}ds/put`"
                             :modelId="modelId"
                             @added="onUploaded"
                             @removed="onRemoved"
                             style="width: 100%;"></field-upload-ds>
            <span class="error-message"><strong>評価データを選択してください</strong></span>
          </field-container>
        </form-item-container>

        <form-item-container label="評価データの削除" annotation="* 評価に使用したデータをサーバ上から削除します">
          <field-container>
            <field-radio-group v-model="deleteUploadFile" name="deleteUploadFile"
                               :options="[{label: '削除しない', value: false}, {label: '削除する', value: true}]"></field-radio-group>
          </field-container>
        </form-item-container>

        <form-item-container label="予測対象" v-if="ds.header && ds.header.length > 0">
          <field-container>
            <field-select name="type" v-model="yName" placeholder="予測対象を選択してください" validate="required"
                          validateLabel="予測対象" @change="onChangeYName(yName)">
              <el-option v-for="column in ds.header" :label="column" :value="column"></el-option>
            </field-select>
          </field-container>
        </form-item-container>
      </form-container>

      <predict-evaluation-result-dialog v-if="predictResultEvaluation != null && visiblePredictEvaluationResult"
                                        :visible="visiblePredictEvaluationResult"
                                        :modelName="modelInfo.modelName"
                                        :uploadFileName="uploadFileName"
                                        :predictEvaluationResult="predictResultEvaluation"
                                        @close="visiblePredictEvaluationResult=false">
      </predict-evaluation-result-dialog>
    </div>

    <template slot="main-footer">
      <el-button class="large" type="default" @click="onClickPredictResultList">戻る</el-button>
      <el-button class="large" type="primary" @click="onClickPredictResultEvaluationList">評価結果一覧</el-button>
      <el-button class="large" type="info" @click="visiblePredictEvaluationResult=true"
                 v-show="predictResultEvaluation != null">結果を表示
      </el-button>
      <el-button class="large" type="primary" @click="onClickPredict">適用結果を評価</el-button>
    </template>
  </main-container>
</template>

<script lang="js">
import BaseView from '../base'
import PredictResultEvaluationDialogCmp
  from '../../components/model/predict-evaluation/dialogs/predict-evaluation-result.vue'
import WebApiClient from '../../api/web'
import { ModelDetailInfo } from '../../defines/model/model'
import { Code } from '../../api/base'
import CommonUtil from '../../utils/common'
import MessageBox from '../../utils/message-box'
import { Category } from '../../defines/const'
import AlgorithmUtil from '../../utils/algorithm'

export default {
  name: 'ModelPredictEvaluationsView',
  mixins: [BaseView],
  components: {
    'predict-evaluation-result-dialog': PredictResultEvaluationDialogCmp
  },
  props: {
    modelId: '',
    modelName: ''
  },
  computed: {
    dsLabelTip () {
      return `モデルの予測値と実績値を投入し、予測結果の評価を得るためのデータです。
                         ファイル形式は、CSVファイルにのみ対応しています。
                         "key"または"id"という項目名を付けると、データを識別する項目として選択されます。
                         なければ、各データに連番を付与します。`
    }
  },
  data () {
    return {
      modelInfo: ModelDetailInfo,
      ds: {
        dsName: null,
        header: [],
        uploadFiles: []
      },
      uploadFileName: '',
      yName: '',
      deleteUploadFile: false,
      dsValidateError: false,
      predictResultEvaluation: {},
      visiblePredictEvaluationResult: false
    }
  },
  created () {
    WebApiClient.getModel(this, this.modelId).then(response => {
      if (response.data.code === Code.SUCCESS) {
        this.modelInfo = response.data.result
        this.modelName = this.modelInfo.modelName
        const purpose = AlgorithmUtil.algorithm2Purpose(this.modelInfo.categoryId,
          this.modelInfo.algorithm.algorithmId)
        if (purpose !== Category.REG && ((Category.CLF === purpose) &&
            (CommonUtil.isEmpty(this.modelInfo.yClasses) || this.modelInfo.yClasses.length > 2))) {
          MessageBox.alert(this, 'このモデルは適用結果の評価ができません。').then(() => {
            this.changeLocation('model-list', null, false, true)
          }).catch(() => {})
          return
        }
        this.$forceUpdate()
      }
    })
  },
  methods: {
    onUploaded (ds) {
      this.uploadFileName = ds.uploadFiles.slice(-1)[0].originalFileName
      this.dsValidateError = false

      if (ds.header.indexOf(this.modelInfo.yName) >= 0) {
        this.yName = this.modelInfo.yName
      } else {
        this.yName = ds.header.slice(-1)[0]
      }
    },
    onRemoved () {
      this.dsValidateError = true
      this.yName = ''
    },
    onChangeYName () {
    },
    buildModelPredictEvaluationParam () {
      return {
        dsName: [this.ds.dsName],
        categoryId: this.modelInfo.categoryId,
        algorithmId: this.modelInfo.algorithm.algorithmId,
        yName: this.yName,
        xCount: 0,
        modelId: this.modelId,
        deleteUploadFile: this.deleteUploadFile
      }
    },
    isValid () {
      this.dsValidateError = (CommonUtil.isEmpty(this.ds.dsName))

      return new Promise((resolve) => {
        this.$validator.validateAll().then((result) => {
          resolve(!this.dsValidateError && result)
        })
      })
    },
    onClickPredict () {
      this.isValid().then(valid => {
        if (!valid) return

        WebApiClient.postModelPredictEvaluation(this, this.modelId, this.buildModelPredictEvaluationParam())
          .then((response) => {
            if (response.data.code === Code.SUCCESS) {
              this.predictResultEvaluation = response.data.result
              this.visiblePredictEvaluationResult = true
              this.$forceUpdate()
            } else if (response.data.code === Code.TIMEOUT) {
              this.onClickPredictResultEvaluationList()
            }
          })
      })
    },
    onClickPredictResultList () {
      this.changeLocation('model-predict-result-list', {
        modelId: this.modelId,
        modelName: this.modelName
      }, false, true)
    },
    onClickPredictResultEvaluationList () {
      this.changeLocation('model-predict-evaluation-result-list', {
        modelId: this.modelId,
        modelName: this.modelName
      }, false, true)
    }
  }
}

</script>

<style scoped>
  .model-predict-evaluation-container >>> .main-content {
    padding: 24px;
  }
</style>



// WEBPACK FOOTER //
// src/views/model/predict-evaluation.vue